import React, { Suspense, lazy, useState } from "react";
import ErrorBoundary from "./components/boundaries/ErrorBoundary";
import "./App.css";

import LoadingState from "./components/common/LoadingState";
import StatusBanner from "./components/common/StatusBanner";
import AppHeader from "./components/layout/AppHeader";
import DatasetMetaPanel from "./components/layout/DatasetMetaPanel";

import { useMarketplaceDataset } from "./hooks/useMarketplaceDataset";
import { useProductHuntDataset } from "./hooks/useProductHuntDataset";
import { DATASET_PATH } from "./config/appConfig";

const AverageRatingDistributionChart = lazy(() =>
  import("./charts/AverageRatingDistributionChart")
);

const TopProductsCharts = lazy(() => import("./charts/TopProductsCharts"));
const LaunchImpactFunnel = lazy(() => import("./charts/LaunchImpactFunnel"));

const KpiOverview = lazy(() => import("./components/overview/KpiOverview"));
const HiddenGemsScatterChart = lazy(() =>
  import("./charts/HiddenGemsScatterChart")
);

const FollowersVsUsageScatter = lazy(() =>
  import("./charts/FollowersVsUsageScatter")
);
const HypeDecayCurve = lazy(() => import("./charts/HypeDecayCurve"));
const AwardImpactCurve = lazy(() => import("./charts/AwardImpactCurve"));

const CategoryDominanceTreemap = lazy(() =>
  import("./charts/CategoryDominanceTreemap")
);

const ProductsLaunchedOverTime = lazy(() =>
  import("./charts/ProductsLaunchedOverTime")
);
const AwardDistributionChart = lazy(() =>
  import("./charts/AwardDistributionChart")
);
const FollowersVsUsageBubbleChart = lazy(() =>
  import("./charts/FollowersVsUsageBubbleChart")
);

export default function App() {
  const [showFields, setShowFields] = useState(false);

  const { rows, metadata, status, error, hasData, refresh } =
    useMarketplaceDataset(DATASET_PATH);

  const productHuntData = useProductHuntDataset(DATASET_PATH);

  /* ================= INITIAL STATES ================= */
  const initialLoading = status === "loading" && !hasData;
  const initialError = status === "error" && !hasData;

  /* ================= MAIN APP ================= */
  return (
    <div className="app-container">
      {/* ================= HEADER ================= */}
      <AppHeader
        metadata={metadata}
        showColumns={showFields}
        onToggleColumns={() => setShowFields((p) => !p)}
        isLoadingMetadata={status === "loading"}
        onRefresh={refresh}
        isRefreshing={status === "loading"}
      />

      {/* ================= DATASET META ================= */}
      <DatasetMetaPanel metadata={metadata} visible={showFields} />

      {/* ================= WARNING ================= */}
      {error && hasData && (
        <StatusBanner
          variant="warning"
          title="Showing cached data"
          message="Latest refresh failed. Using last successful dataset."
          actionLabel="Retry"
          onAction={refresh}
        />
      )}

      {/* ================= INITIAL LOADING / ERROR ================= */}
      {initialLoading && (
        <LoadingState
          label="Loading dataset"
          description="Fetching GitHub Actions marketplace data…"
        />
      )}
      {initialError && (
        <StatusBanner
          variant="error"
          title="Dataset load failed"
          message={error ? String(error) : "Unknown error"}
          actionLabel="Retry"
          onAction={refresh}
        />
      )}

      {/* ================= CONTENT ================= */}
      <ErrorBoundary>
        {/* Render headings immediately; lazy-load each chart individually so headings appear during tests */}
        <div className="row card">
          <Suspense
            fallback={
              <LoadingState
                label="Building dashboard"
                description="Preparing insights and charts…"
              />
            }
          >
            <KpiOverview rows={productHuntData.rows} />
          </Suspense>
        </div>

        <h2 className="section-heading">Top Product Hunt Products</h2>
        <div className="row card">
          <Suspense
            fallback={
              <LoadingState
                label="Building dashboard"
                description="Preparing insights and charts…"
              />
            }
          >
            <TopProductsCharts rows={productHuntData.rows} />
          </Suspense>
        </div>

        <h2 className="section-heading">Launch & Growth Trends</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <ProductsLaunchedOverTime rows={productHuntData.rows} />
          </Suspense>
        </div>
        <h2 className="section-heading">Awards & Recognition Analysis</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <AwardDistributionChart rows={productHuntData.rows} />
          </Suspense>
        </div>
        <h2 className="section-heading">Awards & Recognition Analysis</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <FollowersVsUsageBubbleChart rows={productHuntData.rows} />
          </Suspense>
        </div>
        <h2 className="section-heading">Category Dominance</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <CategoryDominanceTreemap rows={productHuntData.rows} />
          </Suspense>
        </div>

        <h2 className="section-heading">Product Quality & Trust</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <AverageRatingDistributionChart rows={productHuntData.rows} />
          </Suspense>
        </div>
        <h2 className="section-heading">Award Impact Analysis</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <AwardImpactCurve rows={productHuntData.rows} />
          </Suspense>
        </div>
        <h2 className="section-heading">Product Lifecycle Funnel</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <LaunchImpactFunnel rows={productHuntData.rows} />
          </Suspense>
        </div>
        <h2 className="section-heading">Hidden Gems</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <HiddenGemsScatterChart rows={productHuntData.rows} />
          </Suspense>
        </div>

        <h2 className="section-heading">Hype Sustainability Analysis</h2>
        <div className="row card">
          <Suspense fallback={<LoadingState label="Loading chart…" />}>
            <HypeDecayCurve rows={productHuntData.rows} />
          </Suspense>
        </div>
      </ErrorBoundary>
    </div>
  );
}
